import React, { useState } from 'react'
import { Modal, ModalClose, ModalContent } from '@mc/design-system'
import { BREAKPOINTS, useMediaQuery } from '@mc/media'
import {
  CLIENT_APPLICATION_NAMES,
  PLAYER_NAME_TYPES,
  TRACK_PROPS,
  TrackContentType,
} from '@mc/track-event'
import {
  useVideo,
  VideoAutoplay,
  VideoEventHandlers,
  VideoOverlay,
} from '@mc/video'
import { VideoPlayer } from 'components/VideoPlayer'
import { Layout } from '../../common/Layout'

interface Props {
  show?: boolean
  onCloseModal: () => void
  mediaUuid: string
  contentId: string | number
  contentTitle: string
  contentType: TrackContentType
  pauseScreen?: React.ReactNode
}

export const VideoModal = ({
  show,
  onCloseModal,
  mediaUuid,
  contentId,
  contentTitle,
  contentType,
  pauseScreen,
}: Props) => {
  const [showVideoOverlayMobile, setShowVideoOverlayMobile] = useState(false)
  // Matches `d-sm-none`; the overlay is a portalled Modal, so a CSS wrapper can't hide it
  const isMobile = useMediaQuery(`(max-width: ${BREAKPOINTS.SM - 0.02}px)`)

  if (!mediaUuid) return <></>

  const handleOnPause = () => {
    setShowVideoOverlayMobile(true)
  }

  const handleOnCloseVideoOverlayMobile = () => {
    setShowVideoOverlayMobile(false)
    onCloseModal()
  }

  return (
    <Modal
      backdrop='extra-dark'
      show={show}
      onClose={onCloseModal}
      canClickOutsideToClose={!(isMobile && showVideoOverlayMobile)}
    >
      {/* `static` keeps ModalClose positioned against the modal viewport, not the video */}
      <ModalContent aria-label={contentTitle} className='static'>
        <ModalClose />
        <Layout>
          <VideoPlayer
            contentId={contentId}
            contentTitle={contentTitle}
            contentType={contentType}
            mediaUuid={mediaUuid}
            extraTrackingParams={{
              [TRACK_PROPS.PLAYER_NAME]: PLAYER_NAME_TYPES.CERTS,
              [TRACK_PROPS.VIEW_CLIENT_APPLICATION_NAME]:
                CLIENT_APPLICATION_NAMES.CERTS,
            }}
          >
            <VideoEventHandlers onPause={handleOnPause} />
            <VideoAutoplay />
            {pauseScreen && (
              <>
                <VideoOverlay
                  className='d-none d-sm-flex'
                  state={['paused', 'ended']}
                  fade
                >
                  {pauseScreen}
                </VideoOverlay>
                {isMobile && (
                  <VideoOverlayMobile
                    show={showVideoOverlayMobile}
                    onClose={handleOnCloseVideoOverlayMobile}
                  >
                    {pauseScreen}
                  </VideoOverlayMobile>
                )}
              </>
            )}
          </VideoPlayer>
        </Layout>
      </ModalContent>
    </Modal>
  )
}

const VideoOverlayMobile = ({
  show,
  children,
  onClose,
}: {
  show: boolean
  children: React.ReactNode
  onClose: () => void
}) => {
  const { state } = useVideo()
  const showModal = state === 'paused' || state === 'ended'

  return (
    <Modal backdrop='extra-dark' show={show && showModal} onClose={onClose}>
      <ModalClose />
      <Layout>
        <ModalContent>{children}</ModalContent>
      </Layout>
    </Modal>
  )
}
